import React, { useMemo } from 'react';
import { TextField, CircularProgress, Box } from '@mui/material';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { useProductForm, ProductItem, Product } from './ProductForm.hooks';
import { DecimalNumberInput } from '../../../ui';
import {
  ProductFormContainer,
  ProductFormHeader,
  ProductRowNumber,
  PieceRangeLabel,
  RemoveButton,
  FormFieldsContainer,
  QuantityContainer,
  StyledAutocomplete,
  ErrorMessage,
} from './ProductForm.styles';

export interface ProductFormProps {
  item: ProductItem;
  index: number;
  products: Product[];
  canRemove: boolean;
  onRemove: (id: number) => void;
  onUpdate: (id: number, field: keyof ProductItem, value: string) => void;
  selectedProductIds?: number[];
  disabled?: boolean;
  /** Kept for compatibility with parent props; not used for product filtering. */
  supplierId?: string;
}

const ProductForm: React.FC<ProductFormProps> = ({
  item,
  index,
  products,
  canRemove,
  onRemove,
  onUpdate,
  selectedProductIds = [],
  disabled = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  supplierId,
}) => {
  const {
    availableProducts,
    handleProductSelect,
    handleQuantityChange,
    handleRemove,
    isValid,
    showQuantityError,
    hasAvailableProducts,
    selectedProduct,
    isCurrentProductAvailable,
    searchInput,
    setSearchInput,
    asyncLoading,
    asyncHasMore,
    asyncLoadMore,
    useAsyncSearch,
  } = useProductForm({
    item,
    index,
    products,
    canRemove,
    onRemove,
    onUpdate,
    selectedProductIds,
    useAsyncSearch: true,
  });

  // Ensure selected product is always in the list
  const productsWithSelected = useMemo(() => {
    if (!selectedProduct) {
      return availableProducts;
    }

    const hasActiveSearch = useAsyncSearch && searchInput.trim().length > 0;
    if (hasActiveSearch) {
      // While searching, do not force-insert current selection into options.
      // This avoids re-selecting the previous item when user tries to pick another one.
      return availableProducts;
    }

    // Check if selected product is already in the list (by ID, not reference)
    const isInList = availableProducts.some((p) => p.id === selectedProduct.id);

    if (isInList) {
      // If it's in the list, return as-is (will use reference from availableProducts)
      return availableProducts;
    }

    // If not in list, add it at the beginning
    return [selectedProduct, ...availableProducts];
  }, [availableProducts, selectedProduct, useAsyncSearch, searchInput]);

  // Fix: Ensure selectedProduct matches an option in productsWithSelected for reference equality
  const displayValue = useMemo(() => {
    if (!selectedProduct) return null;

    // Find the exact product from productsWithSelected to ensure reference equality
    const productFromList = productsWithSelected.find((p) => p.id === selectedProduct.id);
    return productFromList ?? selectedProduct;
  }, [selectedProduct, productsWithSelected]);

  const pieceCount = Math.trunc(Number(item.quantity));
  const hasValidPieceCount = Number.isFinite(pieceCount) && pieceCount >= 1;
  const pieceRangeLabel = !hasValidPieceCount
    ? null
    : pieceCount === 1
      ? 'Pieza 1'
      : `Piezas 1–${pieceCount}`;

  return (
    <ProductFormContainer>
      <ProductFormHeader>
        <ProductRowNumber>{index + 1}.</ProductRowNumber>
        {pieceRangeLabel && <PieceRangeLabel>{pieceRangeLabel}</PieceRangeLabel>}
      </ProductFormHeader>

      <FormFieldsContainer>
        {/* Product Selection */}
        <StyledAutocomplete
          options={productsWithSelected}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return `${option.name} (${option.code})`;
          }}
          isOptionEqualToValue={(option, value) => {
            if (!value) return false;
            return option.id === value.id;
          }}
          value={displayValue}
          onChange={(_, newValue) => {
            handleProductSelect(newValue);
            if (useAsyncSearch) {
              // After selecting, clear query so next open/search starts clean.
              setSearchInput('');
            }
          }}
          onInputChange={(_, newInputValue, reason) => {
            if (!useAsyncSearch) return;
            if (reason === 'input') {
              // If there is already a selected product, typing means user wants to replace it.
              // Clear current selection first so old value doesn't get re-applied.
              if (selectedProduct) {
                handleProductSelect(null);
              }
              setSearchInput(newInputValue);
            } else if (reason === 'clear') {
              handleProductSelect(null);
              setSearchInput('');
            }
            // Intentionally ignore 'reset' to avoid setting search to selected label.
          }}
          loading={asyncLoading}
          filterOptions={(options) => (useAsyncSearch ? options : options)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Producto"
              placeholder={
                useAsyncSearch ? 'Buscar por nombre o código...' : 'Seleccionar producto'
              }
              required
              error={!isCurrentProductAvailable && item.productName !== ''}
              helperText={
                !isCurrentProductAvailable && item.productName !== ''
                  ? 'Producto no disponible'
                  : useAsyncSearch && searchInput
                    ? 'Escribe para buscar productos...'
                    : ''
              }
              disabled={disabled}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {asyncLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{option.code}</Box>
              </Box>
            </li>
          )}
          componentsProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 4],
                  },
                },
              ],
            },
          }}
          ListboxProps={{
            style: { maxHeight: '300px' },
            onScroll: (event) => {
              if (!useAsyncSearch || !asyncHasMore || asyncLoading) return;
              const listboxNode = event.currentTarget as HTMLElement;
              const nearBottom =
                listboxNode.scrollHeight - listboxNode.scrollTop - listboxNode.clientHeight < 80;
              if (nearBottom) {
                asyncLoadMore();
              }
            },
          }}
          disabled={disabled || (!hasAvailableProducts && !useAsyncSearch && !selectedProduct)}
          noOptionsText={
            asyncLoading
              ? 'Buscando productos...'
              : searchInput && useAsyncSearch
                ? 'No se encontraron productos'
                : hasAvailableProducts
                  ? 'No hay productos disponibles'
                  : 'No hay productos para seleccionar'
          }
        />

        {/* Quantity with Remove Button */}
        <QuantityContainer>
          <DecimalNumberInput
            label="Cantidad"
            value={item.quantity}
            onChange={(val) => handleQuantityChange(val != null ? String(Math.trunc(val)) : '')}
            required
            error={showQuantityError}
            helperText={showQuantityError ? 'Cantidad debe ser mayor a 0' : ''}
            disabled={disabled}
            decimalPlaces={0}
            min={1}
          />
          {canRemove && (
            <RemoveButton onClick={handleRemove} aria-label="Remove product" size="small">
              <DeleteIcon fontSize="small" />
            </RemoveButton>
          )}
        </QuantityContainer>
      </FormFieldsContainer>

      {/* Validation Messages */}
      {!isValid && item.productName && item.quantity && (
        <ErrorMessage>Verifica que todos los campos estén completos</ErrorMessage>
      )}
    </ProductFormContainer>
  );
};

export default ProductForm;
